/**
 * A jornada da abertura com o caso de entrada aberto (E144.F3/F4, GUS-105 + GUS-135): Enviar
 * documentos → Conferir → Acompanhar, a mesma no portal (escritório e banco) e na área do perito. A
 * página guarda o passo na URL (recarregar volta ao mesmo passo) e decide para onde vão "Abrir
 * caso", "Novo caso", "Voltar aos casos" e o caso que não é da eletrônica.
 *
 * E163.F3 (GUS-209 A67-13/14): a consulta "Cadeia de custódia" é um destino da jornada, fora das
 * etapas (`custodia`, guardada na URL pela página): o "Ver recibo" abre, "Voltar" devolve ao mesmo
 * passo, com as respostas sobre os apoios guardadas aqui (F-E163.F3-01). E165.F1.T03: o "Ver
 * recibo" fica no Enviar documentos: a custódia saiu do Conferir (T05, GUS-202).
 *
 * E189.F2 (ADR-0138): o Conferir com o cadastro sem espera — "Enviar e seguir" devolve aos casos
 * ("Voltar aos casos" da página), e o caso com o pedido em andamento mostra o círculo.
 */
import { useState } from 'react';
import type { AxiosInstance } from 'axios';

import { ptBR } from '@/i18n/pt-BR';
import type { PassoDaAbertura } from '@/lib/abertura';
import type { EscolhaDoContrato } from '@/lib/abertura_do_caso';
import type { CaseRecordData } from '@/lib/cases';
import type { ConclusaoDaAberturaData } from '@/lib/conclusao_da_abertura';

import { AberturaFrame } from './AberturaFrame';
import { AcompanharEnvio } from './AcompanharEnvio';
import { CadeiaDeCustodia } from './CadeiaDeCustodia';
import { ConferirOuCadastrando } from './ConferirOuCadastrando';
import { ContratosDigitais } from './ContratosDigitais';
import { EnviarComCaso } from './EnviarComCaso';
import { eyebrowDaAbertura } from './rotulosDoEnvio';

const t = ptBR.abertura;

const TITULOS: Record<PassoDaAbertura, { titulo: string; subtitulo: string }> = {
  enviar: { titulo: t.titulo, subtitulo: t.subtitulo },
  contratos: t.contratosDigitais,
  conferir: t.conferirEnvio,
  acompanhar: t.acompanhar,
};

export interface JornadaDaAberturaProps {
  client: AxiosInstance;
  caseId: string;
  passo: PassoDaAbertura;
  onPasso: (passo: PassoDaAbertura) => void;
  onAbrirCaso: (caseId: string) => void;
  onNovoCaso: () => void;
  onSair: () => void;
  /** O caso não é da eletrônica: a página o leva ao envio de sempre. */
  onOutroServico: (caso: CaseRecordData) => void;
  /**
   * E143.F2 (ADR-0097): a abertura pelo banco. Os passos são os mesmos; as ações que seguem com a
   * equipe (reconferir a custódia, resolver ocorrência, tentar a leitura de novo) não aparecem.
   */
  doBanco?: boolean;
  /**
   * E162.F2.T04 (QA-16): a abertura do cliente (portal, escritório e banco). O diagnóstico do
   * material — o que o comprovante preencheu, o fuso, os recursos automáticos do arquivo — fica com a
   * equipe; o cliente vê a situação e o impacto.
   */
  doCliente?: boolean;
  /** E163.F3: a consulta "Cadeia de custódia" aberta sobre o passo; a página guarda na URL. */
  custodia?: boolean;
  onCustodia?: (aberta: boolean) => void;
}

/**
 * F-E163.F3-01: a consulta da custódia desmonta o Conferir; as respostas ficam aqui, por caso. As
 * escolhas das CCBs que já têm caso (E165.F1.T06) também.
 */
function useGuardadoDoCaso<V extends string>(
  caseId: string,
): [Record<string, V>, (valores: Record<string, V>) => void] {
  const [guardados, setGuardados] = useState({ caseId, valores: {} as Record<string, V> });
  return [
    guardados.caseId === caseId ? guardados.valores : {},
    (valores) => setGuardados({ caseId, valores }),
  ];
}

export function JornadaDaAbertura(props: JornadaDaAberturaProps): JSX.Element {
  const { client, caseId, passo, onPasso } = props;
  const doBanco = props.doBanco ?? false;
  const doCliente = props.doCliente ?? false;
  const [conclusao, setConclusao] = useState<ConclusaoDaAberturaData | null>(null);
  const [respostas, setRespostas] = useGuardadoDoCaso<string>(caseId);
  const [escolhas, setEscolhas] = useGuardadoDoCaso<EscolhaDoContrato>(caseId);
  const onCustodia = props.onCustodia ?? (() => undefined);
  if (props.custodia === true) {
    return (
      <AberturaFrame
        passo={passo}
        eyebrow={t.custodia.paginaEyebrow}
        titulo={t.custodia.paginaTitulo}
        subtitulo={t.custodia.paginaSubtitulo}
        semEtapas
      >
        <CadeiaDeCustodia
          client={client}
          caseId={caseId}
          doBanco={doBanco}
          doCliente={doCliente}
          onVoltar={() => onCustodia(false)}
        />
      </AberturaFrame>
    );
  }
  // E165.F1.T07 (ADR-0114 §4): o Acompanhar é "Envio recebido", vinculando ou abrindo caso.
  const titulos = TITULOS[passo];
  // E165.F1.T03 (HTML 6.8): o rótulo diz quem abre (banco ou escritório).
  const eyebrow = eyebrowDaAbertura({ doBanco, doCliente });
  return (
    <AberturaFrame passo={passo} eyebrow={eyebrow} {...titulos}>
      {passo === 'enviar' ? (
        <EnviarComCaso
          client={client}
          caseId={caseId}
          onContinuar={() => onPasso('contratos')}
          onVoltar={props.onSair}
          onOutroServico={props.onOutroServico}
          doCliente={doCliente}
        />
      ) : null}
      {passo === 'contratos' ? (
        <ContratosDigitais
          client={client}
          caseId={caseId}
          onVoltar={() => onPasso('enviar')}
          onConferir={() => onPasso('conferir')}
          onVerRecibo={() => onCustodia(true)}
        />
      ) : null}
      {passo === 'conferir' ? (
        <ConferirOuCadastrando
          client={client}
          caseId={caseId}
          onVoltarAosCasos={props.onSair}
          onNovoCaso={props.onNovoCaso}
          onVoltar={() => onPasso('contratos')}
          onAbrirCaso={props.onAbrirCaso}
          respostas={respostas}
          onRespostas={setRespostas}
          escolhas={escolhas}
          onEscolhas={setEscolhas}
          onConcluido={(recibo) => {
            setConclusao(recibo);
            onPasso('acompanhar');
          }}
        />
      ) : null}
      {passo === 'acompanhar' ? (
        <AcompanharEnvio
          client={client}
          caseId={caseId}
          conclusao={conclusao}
          onLida={setConclusao}
          onAbrirCaso={props.onAbrirCaso}
          onVoltarAosCasos={props.onSair}
          onNovoCaso={props.onNovoCaso}
          onConferir={() => onPasso('conferir')}
        />
      ) : null}
    </AberturaFrame>
  );
}
